import { useState, useCallback } from "react";

interface AquapilotDeviceContext {
  scanForLifeguard: () => Promise<void>;
  connectToDevice: (device: BluetoothDevice) => Promise<void>;
  disconnectFromDevice: () => void;
  writeCharacteristic: (value: string) => Promise<void>;
  readCharacteristic: () => Promise<string | null>;
  sendWifiCredentials: (
    server: BluetoothRemoteGATTServer,
    serviceUUID: string,
    ssidCharacteristicUUID: string,
    passwordCharacteristicUUID: string,
    encMethodCharacteristicUUID: string,
    ssid: string,
    password: string
  ) => Promise<void>;

  connectedDevice: BluetoothDevice | null;
  isConnected: boolean;
  loading: boolean;
}

export const UUID_WIFI_SERVICE = "ff00c161-5d1a-4c5d-83f6-0671df21574d";
export const UUID_CHAR_WIFI_SSID = "0000ff02-0000-1000-8000-00805f9b34fb";
export const UUID_CHAR_WIFI_PASSWORD = "0000ff04-0000-1000-8000-00805f9b34fb";
export const UUID_CHAR_WIFI_ENC_METHOD = "0000ff03-0000-1000-8000-00805f9b34fb";

export function useBLEforAquapilotWeb(): AquapilotDeviceContext {
  const [connectedDevice, setConnectedDevice] =
    useState<BluetoothDevice | null>(null);
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Adjusting the scanForLifeguard function to automatically connect to the chosen device
  const scanForLifeguard = useCallback(async () => {
    try {
      setLoading(true);

      // Request the Bluetooth device with improved filters
      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: "dlgt" },
          { namePrefix: "dwlg" },
          { namePrefix: "hwqh" },
        ],
        optionalServices: [
          UUID_WIFI_SERVICE, // UUID_WIFI_SERVICE
        ],
      });

      if (device) {
        console.log("Device found:", device.name);
        setConnectedDevice(device);

        // Automatically connect to the device
        const gattServer = await device.gatt?.connect();
        if (!gattServer) throw new Error("Failed to connect to GATT Server.");

        console.log("Connected to device:", device.name);
        setServer(gattServer);
        setIsConnected(true);
      } else {
        console.error("No device found.");
      }
    } catch (error) {
      console.error("Error scanning for devices:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const connectToDevice = useCallback(async (device: BluetoothDevice) => {
    try {
      setLoading(true);
      const gattServer = await device.gatt?.connect();
      console.log({ gattServer });
      if (!gattServer) throw new Error("Failed to connect to GATT Server.");

      console.log("Connected to device:", device.name);
      setServer(gattServer);
      setIsConnected(true);
    } catch (error) {
      console.error("Error connecting to device:", error);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const disconnectFromDevice = useCallback(() => {
    if (server) {
      server.disconnect();
      console.log("Disconnected from device");
      setServer(null);
      setConnectedDevice(null);
      setIsConnected(false);
    }
  }, [server]);

  const writeCharacteristic = useCallback(
    async (value: string) => {
      if (!server) {
        console.error("No connected GATT server");
        return;
      }

      try {
        setLoading(true);
        const service = await server.getPrimaryService(
          "ff00c161-5d1a-4c5d-83f6-0671df21574d" // Substitua pelo UUID do seu serviço
        );
        const characteristic = await service.getCharacteristic(
          "0000ff02-0000-1000-8000-00805f9b34fb" // Substitua pelo UUID da sua característica
        );
        const encodedValue = new TextEncoder().encode(value);
        await characteristic.writeValue(encodedValue);
        console.log("Value written successfully");
      } catch (error) {
        console.error("Error writing to characteristic:", error);
      } finally {
        setLoading(false);
      }
    },
    [server]
  );

  async function sendWifiCredentials(
    server: BluetoothRemoteGATTServer,
    serviceUUID: string,
    ssidCharacteristicUUID: string,
    passwordCharacteristicUUID: string,
    encMethodCharacteristicUUID: string,
    ssid: string,
    password: string
  ) {
    try {
      const ssidBase64 = new TextEncoder().encode(ssid);
      const passwordBase64 = new TextEncoder().encode(password);

      // Descobre o serviço e as características
      const service = await server.getPrimaryService(serviceUUID);

      const ssidCharacteristic = await service.getCharacteristic(
        ssidCharacteristicUUID
      );
      const passwordCharacteristic = await service.getCharacteristic(
        passwordCharacteristicUUID
      );

      console.log("Discovered services and characteristics");

      // Envia o SSID
      await ssidCharacteristic.writeValue(ssidBase64);
      console.log("WiFi SSID written");

      // Envia a senha
      await passwordCharacteristic.writeValue(passwordBase64);
      console.log("WiFi password written");

      // Reseta o WiFi
      await resetWifi(server, serviceUUID, encMethodCharacteristicUUID);
      console.log("WiFi reset successfully");
    } catch (error) {
      console.error("[WebBLEService] Error sending WiFi credentials:", error);
    }
  }

  async function resetWifi(
    server: BluetoothRemoteGATTServer,
    serviceUUID: string,
    characteristicUUID: string
  ) {
    try {
      const resetValue = new Uint8Array([3]);

      const service = await server.getPrimaryService(serviceUUID);
      const characteristic = await service.getCharacteristic(
        characteristicUUID
      );

      // Envia o valor codificado para resetar
      await characteristic.writeValue(resetValue);

      console.log("[WebBLEService] WiFi reset command sent successfully.");
    } catch (error) {
      console.error("[WebBLEService] Error resetting WiFi:", error);
    }
  }

  const readCharacteristic = useCallback(async (): Promise<string | null> => {
    if (!server) {
      console.error("No connected GATT server");
      return null;
    }

    try {
      setLoading(true);
      const service = await server.getPrimaryService(
        "ff00c161-5d1a-4c5d-83f6-0671df21574d" // Substitua pelo UUID do seu serviço
      );
      const characteristic = await service.getCharacteristic(
        "0000ff02-0000-1000-8000-00805f9b34fb" // Substitua pelo UUID da sua característica
      );
      const value = await characteristic.readValue();
      const decodedValue = new TextDecoder().decode(value);
      console.log("Read value:", decodedValue);
      return decodedValue;
    } catch (error) {
      console.error("Error reading characteristic:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [server]);

  return {
    scanForLifeguard,
    connectToDevice,
    sendWifiCredentials,
    disconnectFromDevice,
    writeCharacteristic,
    readCharacteristic,
    connectedDevice,
    isConnected,
    loading,
  };
}
